'use client';

import React from 'react';
import { ArrowRight, ChevronRight, Layers } from 'lucide-react';
import { PrismicRichText } from '@prismicio/react';
import TransitionLink from './TransitionLink';
import { NavIcon } from './NavIcon';
import { NavBadge } from './NavBadge';
import type { NavLabels, NavSection } from '@/lib/get-navbar-data';

type StandardSection = Extract<NavSection, { type: 'standard' }>;

interface NavServicesMegaProps {
  section: StandardSection;
  lang: string;
  onClose: () => void;
  labels: NavLabels;
}

export function NavServicesMega({ section, lang, onClose, labels }: NavServicesMegaProps) {
  const featured = section.items.filter((i) => i.isFeatured);
  // 1 featured → big horizontal banner; ≥2 → grid of stacked cards above the row list
  const flagship = featured.length === 1 ? featured[0] : null;
  const featuredGrid = featured.length >= 2 ? featured : [];
  const rows = section.items.filter((i) => !featured.includes(i));

  return (
    <div className="grid gap-14 lg:grid-cols-[400px_1fr]">
      {/* Left column — eyebrow + title + desc + promo */}
      <div className="flex flex-col gap-4">
        {section.eyebrow && (
          <p className="m-0 text-xs font-bold uppercase tracking-[0.16em] text-[#157A73]">
            {section.eyebrow}
          </p>
        )}
        {section.menuTitle && (
          <h2 className="m-0 text-3xl font-normal leading-[1.1] tracking-[-0.02em] text-[#1B2A33]">
            {section.menuTitle}
          </h2>
        )}
        {section.menuDescription && section.menuDescription.length > 0 && (
          <div className="text-[15.5px] leading-[1.55] text-[#5B6B75]">
            <PrismicRichText field={section.menuDescription} />
          </div>
        )}
        {section.promo && (
          <TransitionLink
            href={`/${lang}${section.promo.url}`}
            onClick={onClose}
            className="group relative mt-2 flex items-center gap-4 overflow-hidden rounded-2xl p-4 text-white no-underline transition-transform duration-200 hover:translate-x-1"
            style={{
              background:
                'radial-gradient(circle at 85% -20%, rgba(255,255,255,0.18) 0%, transparent 55%), linear-gradient(155deg, #0F5E58 0%, #1FA39A 100%)',
            }}
          >
            <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-white/20">
              <NavIcon
                keyword={section.promo.iconKey}
                fallback={ChevronRight}
                className="h-5 w-5"
              />
            </span>
            <div className="flex-1">
              <p className="m-0 text-sm font-semibold tracking-[-0.01em]">
                {section.promo.title}
              </p>
              {section.promo.subtitle && (
                <p className="m-0 text-xs text-white/85">{section.promo.subtitle}</p>
              )}
            </div>
            <ChevronRight className="h-4 w-4 opacity-85" />
          </TransitionLink>
        )}
      </div>

      {/* Right column — flagship banner + row grid */}
      <div className="flex flex-col gap-4">
        {flagship && (
          <TransitionLink
            href={`/${lang}${flagship.url}`}
            onClick={onClose}
            className="group grid w-full max-w-[900px] grid-cols-[auto_1fr_auto] items-center gap-7 rounded-2xl border border-[#D1EAE7] p-5 text-inherit no-underline transition-transform duration-200 hover:translate-x-1 sm:p-6"
            style={{
              background: 'linear-gradient(135deg, #E8F4F2 0%, #F4F2EE 100%)',
            }}
          >
            <span className="grid h-[60px] w-[60px] flex-shrink-0 place-items-center rounded-2xl bg-white text-[#157A73] shadow-[0_4px_12px_-4px_rgba(31,163,154,0.25)]">
              <NavIcon
                keyword={flagship.iconKey}
                fallback={Layers}
                className="h-7 w-7"
                strokeWidth={1.7}
              />
            </span>
            <div className="min-w-0">
              <h3 className="m-0 mb-1.5 flex items-center gap-2 text-lg font-semibold leading-[1.15] tracking-[-0.015em] text-[#1B2A33]">
                {flagship.name}
                {flagship.badge && <NavBadge label={flagship.badge} />}
              </h3>
              {flagship.description && (
                <p className="m-0 max-w-[52ch] text-sm leading-[1.5] text-[#5B6B75]">
                  {flagship.description}
                </p>
              )}
            </div>
            <span className="hidden items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-semibold text-[#157A73] transition-all duration-200 group-hover:gap-3 group-hover:text-[#0F5E58] sm:inline-flex">
              {labels.learnMore}
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </TransitionLink>
        )}

        {featuredGrid.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-3">
            {featuredGrid.map((item) => (
              <TransitionLink
                key={item.url}
                href={`/${lang}${item.url}`}
                onClick={onClose}
                className="group flex flex-col gap-3 rounded-2xl border border-[#D1EAE7] p-4 text-inherit no-underline transition-transform duration-200 hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #E8F4F2 0%, #F4F2EE 100%)',
                }}
              >
                <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-white text-[#157A73] shadow-[0_4px_12px_-4px_rgba(31,163,154,0.25)]">
                  <NavIcon keyword={item.iconKey} className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <div className="min-w-0">
                  <h3 className="m-0 mb-1 flex items-center gap-2 text-base font-semibold tracking-[-0.01em] text-[#1B2A33]">
                    {item.name}
                    {item.badge && <NavBadge label={item.badge} />}
                  </h3>
                  {item.description && (
                    <p className="m-0 text-xs leading-[1.4] text-[#5B6B75]">
                      {item.description}
                    </p>
                  )}
                </div>
              </TransitionLink>
            ))}
          </div>
        )}

        <div className="grid gap-1.5 sm:grid-cols-2">
          {rows.map((item) => (
            <TransitionLink
              key={item.url}
              href={`/${lang}${item.url}`}
              onClick={onClose}
              className="group grid grid-cols-[44px_1fr_auto] items-center gap-3.5 rounded-xl p-3 text-inherit no-underline transition-[background,padding] duration-200 hover:bg-white hover:pl-[18px]"
            >
              <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl border border-[#D1EAE7] bg-[#E8F4F2] text-[#157A73] transition-colors duration-200 group-hover:bg-[#D1EAE7]">
                <NavIcon keyword={item.iconKey} className="h-5 w-5" strokeWidth={1.8} />
              </span>
              <div className="min-w-0">
                <p className="m-0 mb-0.5 flex items-center gap-2 text-[15.5px] font-semibold leading-[1.3] tracking-[-0.005em] text-[#1B2A33]">
                  {item.name}
                  {item.badge && <NavBadge label={item.badge} />}
                </p>
                {item.description && (
                  <p className="m-0 truncate text-xs leading-[1.4] text-[#5B6B75]">
                    {item.description}
                  </p>
                )}
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-[#8A99A3] opacity-50 transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#1FA39A] group-hover:opacity-100" />
            </TransitionLink>
          ))}
        </div>
      </div>
    </div>
  );
}
